"use client";

import { useState, useEffect } from "react";
import { useSophonz } from "@/components/sophonz-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

export function ConfigForm() {
  const { config, updateConfig, initSophonz, deinitSophonz, isInitialized } = useSophonz();
  const { toast } = useToast();
  
  const [formValues, setFormValues] = useState({
    collectorUrl: '',
    appName: '',
    appVersion: '',
    appKey: '',
    project: '',
    deploymentEnvironment: '',
  });

  // Update form values when config changes
  useEffect(() => {
    setFormValues({
      collectorUrl: config.collectorUrl || '',
      appName: config.appName || '',
      appVersion: config.appVersion || '',
      appKey: config.appKey || '',
      project: config.project || '',
      deploymentEnvironment: config.deploymentEnvironment || '',
    });
  }, [config]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formValues.collectorUrl || !formValues.appName || !formValues.appVersion || !formValues.appKey) {
      toast({
        title: "Missing required fields",
        description: "All fields are required to initialize Sophonz Browser SDK",
        variant: "destructive",
      });
      return;
    }
    
    // Update config with form values
    updateConfig(formValues);
    
    toast({
      title: "Configuration saved",
      description: "Sophonz Browser SDK configuration has been saved to localStorage",
    });
  };

  const handleToggleAgent = () => {
    if (isInitialized) {
      deinitSophonz();
      toast({
        title: "Sophonz Browser SDK deactivated",
        description: "The agent has been stopped successfully",
      });
    } else {
      if (!formValues.collectorUrl || !formValues.appName || !formValues.appVersion || !formValues.appKey) {
        toast({
          title: "Cannot activate",
          description: "All configuration fields are required to initialize Sophonz Browser SDK",
          variant: "destructive",
        });
        return;
      }
      
      initSophonz();
      toast({
        title: "Sophonz Browser SDK activated",
        description: "The agent has been started successfully",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="collectorUrl">Collector URL</Label>
        <Input
          id="collectorUrl"
          name="collectorUrl"
          placeholder="https://in-otel.sophonz.com"
          value={formValues.collectorUrl}
          onChange={handleChange}
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="appName">App Name</Label>
        <Input
          id="appName"
          name="appName"
          placeholder="My Web Service"
          value={formValues.appName}
          onChange={handleChange}
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="appVersion">App Version</Label>
        <Input
          id="appVersion"
          name="appVersion"
          placeholder="1.0.0"
          value={formValues.appVersion}
          onChange={handleChange}
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="appKey">App Key</Label>
        <Input
          id="appKey"
          name="appKey"
          placeholder="Your Sophonz service key"
          value={formValues.appKey}
          onChange={handleChange}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="project">
          Project
          <span className="text-sm font-normal text-muted-foreground ml-2">(Optional)</span>
        </Label>
        <Input
          id="project"
          name="project"
          placeholder="e.g., sophonz (Optional)"
          value={formValues.project}
          onChange={handleChange}
        />
        <p className="text-sm text-muted-foreground">
          Maps to OTel <code>service.namespace</code>. Groups services in the
          Sophonz dashboard.
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="deploymentEnvironment">
          Deployment Environment
          <span className="text-sm font-normal text-muted-foreground ml-2">(Optional)</span>
        </Label>
        <Input
          id="deploymentEnvironment"
          name="deploymentEnvironment"
          placeholder="e.g., development, staging, production (Optional)"
          value={formValues.deploymentEnvironment}
          onChange={handleChange}
        />
        <p className="text-sm text-muted-foreground">
          Identifies the environment where the application is deployed
        </p>
      </div>

      <div className="flex gap-2">
        <Button type="submit">Save Configuration</Button>
        <Button 
          type="button" 
          variant={isInitialized ? "destructive" : "default"}
          onClick={handleToggleAgent}
        >
          {isInitialized ? "Deactivate Agent" : "Activate Agent"}
        </Button>
      </div>
    </form>
  );
}